"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, EyeOff } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + gestochen scharfer Schwärzungs-Scanner im Vordergrund.
 */
export default function Preview() {
  const [redacted, setRedacted] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setRedacted((v) => !v), 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.45, 0.85, 0.45] }}
        transition={{ duration: 4.5, repeat: Infinity }}
        className={`pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl transition-colors duration-500 ${
          redacted ? "bg-emerald-500/35" : "bg-rose-500/30"
        }`}
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(16,185,129,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(16,185,129,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <motion.div
        animate={{ y: redacted ? -3 : 2 }}
        transition={{ type: "spring", stiffness: 240, damping: 20 }}
        className={`relative z-10 flex w-56 flex-col gap-2 rounded-2xl border bg-white p-3.5 transition-all duration-500 antialiased dark:bg-zinc-900 ${
          redacted
            ? "border-emerald-400/80 shadow-[0_18px_36px_-6px_rgba(16,185,129,0.55)]"
            : "border-rose-400/70 shadow-[0_14px_28px_-6px_rgba(244,63,94,0.4)]"
        }`}
      >
        <div className="flex items-center justify-between text-[10px] font-bold">
          <span className="flex items-center gap-1 text-neutral-500">
            <EyeOff className="h-3 w-3 text-emerald-500" />
            GEHEIM-DATEN
          </span>
          <span
            className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-black ${
              redacted
                ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                : "bg-rose-500/15 text-rose-500"
            }`}
          >
            {redacted ? "Geschwärzt ✓" : "Sichtbar!"}
          </span>
        </div>

        <div className="relative flex items-center justify-between overflow-hidden rounded-lg border border-black/10 bg-neutral-100 px-2.5 py-1.5 font-mono text-xs font-black dark:border-white/10 dark:bg-zinc-800">
          <span className="text-rose-500">DE89 3704 0044</span>

          <motion.div
            animate={{ width: redacted ? "100%" : "0%" }}
            transition={{ duration: 0.45, ease: "easeInOut" }}
            className="absolute inset-y-0 left-0 flex items-center justify-center overflow-hidden bg-neutral-950 text-[10px] text-emerald-400 shadow-[4px_0_12px_#10b981]"
          >
            <ShieldCheck className="mr-1 h-3.5 w-3.5 shrink-0" />
            <span className="tracking-widest">██████████</span>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
